import React, { useEffect, useRef } from 'react';

/**
 * MathRenderer — Renderiza fórmulas matemáticas usando KaTeX (cargado vía CDN en index.html)
 *
 * Detecta automáticamente notación LaTeX en texto:
 *   - Inline:   $x^2 + 2x - 3 = 0$  o  \(...\)
 *   - Display:  $$\frac{d}{dx}$$     o  \[...\]
 *   - Texto sin fórmulas: se renderiza como texto normal
 *
 * Props:
 *   text     {string}  — texto que puede contener fórmulas LaTeX
 *   className {string} — clases CSS adicionales
 *   block    {boolean} — si true, renderiza como bloque (display mode)
 */
const MathRenderer = ({ text, className = '', block = false }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current || !text) return;

    // Si KaTeX no está disponible (sin conexión o CDN no cargó), mostrar texto plano
    if (!window.renderMathInElement && !window.katex) {
      ref.current.textContent = text;
      return;
    }

    // Limpiar contenido previo
    ref.current.innerHTML = '';

    if (window.renderMathInElement) {
      // Auto-render de KaTeX: detecta $...$, $$...$$, \(...\), \[...\]
      ref.current.textContent = text;
      try {
        window.renderMathInElement(ref.current, {
          delimiters: [
            { left: '$$', right: '$$', display: true },
            { left: '$', right: '$', display: false },
            { left: '\\[', right: '\\]', display: true },
            { left: '\\(', right: '\\)', display: false },
          ],
          throwOnError: false,
          errorColor: '#ff5252',
          trust: false,
        });
      } catch (e) {
        ref.current.textContent = text;
      }
    } else if (window.katex && block) {
      // Render de una sola expresión en bloque
      try {
        ref.current.innerHTML = window.katex.renderToString(text, {
          displayMode: true,
          throwOnError: false,
        });
      } catch (e) {
        ref.current.textContent = text;
      }
    } else {
      ref.current.textContent = text;
    }
  }, [text, block]);

  const Tag = block ? 'div' : 'span';

  return (
    <Tag
      ref={ref}
      className={`math-render ${className}`}
      aria-label={text}
    />
  );
};

/**
 * Función utilitaria: detecta si un string es una imagen en base64 o URL
 */
export function isImageDataOrUrl(str) {
  if (!str || typeof str !== 'string') return false;
  const s = str.trim();
  if (/^data:image\/[a-zA-Z0-9+.-]+;base64,/i.test(s)) return true;
  if (/^data:image\/[a-zA-Z0-9+.-]+,/i.test(s)) return true;
  if (/^!\[.*?\]\(.*?\)$/i.test(s)) return true;
  if (/^https?:\/\/[^\s]+?\.(png|jpe?g|gif|svg|webp|bmp|avif)(\?[^\s]*)?(#[^\s]*)?$/i.test(s)) return true;
  if (/^https?:\/\/(firebasestorage\.googleapis\.com|storage\.googleapis\.com)\/[^\s]+/i.test(s)) return true;
  return false;
}

/**
 * Extrae la URL/URI y metadatos de una posible imagen
 */
export function extractImageData(str) {
  if (!str || typeof str !== 'string') return null;
  const s = str.trim();

  // 1. Data URI directa
  if (/^data:image\/[a-zA-Z0-9+.-]+;base64,/i.test(s) || /^data:image\/[a-zA-Z0-9+.-]+,/i.test(s)) {
    return { src: s, alt: 'Opción gráfica' };
  }

  // 2. URL de imagen directa
  if (/^https?:\/\/[^\s]+?\.(png|jpe?g|gif|svg|webp|bmp|avif)(\?[^\s]*)?(#[^\s]*)?$/i.test(s) ||
      /^https?:\/\/(firebasestorage\.googleapis\.com|storage\.googleapis\.com)\/[^\s]+/i.test(s)) {
    return { src: s, alt: 'Opción gráfica' };
  }

  // 3. Sintaxis Markdown ![alt](src)
  const mdMatch = s.match(/^!\[(.*?)\]\((.*?)\)$/);
  if (mdMatch) {
    return { src: mdMatch[2].trim(), alt: mdMatch[1] || 'Opción gráfica' };
  }

  // 4. Data URI embebida dentro de una cadena (ej: "A data:image/png;base64,...")
  const embeddedDataUri = s.match(/(data:image\/[a-zA-Z0-9+.-]+;base64,[A-Za-z0-9+/=]+)/i);
  if (embeddedDataUri) {
    const textLabel = s.replace(embeddedDataUri[0], '').replace(/^[A-Da-d][\).\s\-:]*/, '').trim();
    return { src: embeddedDataUri[0], alt: textLabel || 'Opción gráfica', label: textLabel };
  }

  return null;
}

/**
 * Función utilitaria: detecta si un texto contiene fórmulas LaTeX
 */
export function hasMath(text) {
  if (!text) return false;
  return /\$[\s\S]+?\$|\\\([\s\S]+?\\\)|\\\[[\s\S]+?\\\]/.test(text);
}

/**
 * QuestionText — renderiza el texto de la pregunta con soporte para KaTeX e imágenes
 */
export const QuestionText = ({ text, className = '' }) => {
  if (!text) return null;
  const str = String(text);

  // Si todo el texto es una imagen pura
  const fullImg = extractImageData(str);
  if (fullImg && !fullImg.label) {
    return (
      <div className={`content-image-block ${className}`}>
        <img src={fullImg.src} alt={fullImg.alt} className="question-content-image" loading="lazy" />
      </div>
    );
  }

  // Si contiene data:image o imágenes markdown intercaladas con texto
  const imageRegex = /(!\[(.*?)\]\((data:image\/[^\s)]+|https?:\/\/[^\s)]+)\))|(data:image\/[a-zA-Z0-9+.-]+;base64,[A-Za-z0-9+/=]+)/g;
  if (imageRegex.test(str)) {
    imageRegex.lastIndex = 0;
    const segments = [];
    let lastIdx = 0;
    let match;
    while ((match = imageRegex.exec(str)) !== null) {
      if (match.index > lastIdx) {
        segments.push({ type: 'text', val: str.substring(lastIdx, match.index) });
      }
      if (match[1]) {
        segments.push({ type: 'image', src: match[3], alt: match[2] || 'Gráfica del reactivo' });
      } else if (match[4]) {
        segments.push({ type: 'image', src: match[4], alt: 'Gráfica del reactivo' });
      }
      lastIdx = imageRegex.lastIndex;
    }
    if (lastIdx < str.length) {
      segments.push({ type: 'text', val: str.substring(lastIdx) });
    }

    return (
      <div className={`rich-question-content ${className}`}>
        {segments.map((seg, i) => {
          if (seg.type === 'image') {
            return (
              <div key={i} className="content-image-block">
                <img src={seg.src} alt={seg.alt} className="question-content-image" loading="lazy" />
              </div>
            );
          }
          if (hasMath(seg.val)) {
            return <MathRenderer key={i} text={seg.val} className="question-math-inline" />;
          }
          return <span key={i} className="question-plain-text">{seg.val}</span>;
        })}
      </div>
    );
  }

  if (hasMath(str)) {
    return <MathRenderer text={str} className={`question-math-text ${className}`} />;
  }
  return <span className={`question-plain-text ${className}`}>{str}</span>;
};

/**
 * OptionText — renderiza el contenido de una opción: imagen (base64/URL), LaTeX o texto plano
 */
export const OptionText = ({ text }) => {
  if (!text) return null;
  const str = String(text);

  // 1. Imagen detectada (base64 o URL)
  const imgData = extractImageData(str);
  if (imgData) {
    return (
      <div className="option-image-container">
        {imgData.label && <span className="option-image-label">{imgData.label}</span>}
        <div className="option-image-wrapper">
          <img
            src={imgData.src}
            alt={imgData.alt}
            className="option-image"
            loading="lazy"
            onError={(e) => {
              e.target.style.display = 'none';
              if (e.target.parentNode) {
                e.target.parentNode.innerHTML = '<span class="option-img-error">⚠️ Gráfico no disponible</span>';
              }
            }}
          />
        </div>
      </div>
    );
  }

  // 2. Fórmulas LaTeX
  if (hasMath(str)) {
    return <MathRenderer text={str} className="option-math-text" />;
  }

  // 3. Texto plano
  return <span>{text}</span>;
};

export default MathRenderer;

